import { create } from 'zustand';
import type { MemberProperty } from '@features/properties/api';

const STORAGE_PREFIX = 'resident_active_property';

function storageKey(memberId: string, tenantId: string): string {
  return `${STORAGE_PREFIX}:${tenantId}:${memberId}`;
}

function readStoredPropertyId(memberId: string, tenantId: string): string | null {
  try {
    return localStorage.getItem(storageKey(memberId, tenantId));
  } catch {
    return null;
  }
}

function writeStoredPropertyId(memberId: string, tenantId: string, propertyId: string | null): void {
  try {
    const key = storageKey(memberId, tenantId);
    if (propertyId) {
      localStorage.setItem(key, propertyId);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    /* storage unavailable */
  }
}

interface PropertyContext {
  memberId: string;
  tenantId: string;
}

interface PropertyState {
  activePropertyId: string | null;
  setActivePropertyId: (propertyId: string | null, context?: PropertyContext) => void;
  /**
   * Pick the active property for the current member: persisted choice if still
   * valid, otherwise the first approved property.
   */
  syncActiveProperty: (
    properties: MemberProperty[],
    context: { memberId: string; tenantId: string },
  ) => MemberProperty | null;
  clear: () => void;
}

export const usePropertyStore = create<PropertyState>((set, get) => ({
  activePropertyId: null,

  setActivePropertyId(propertyId, context) {
    set({ activePropertyId: propertyId });
    if (context) {
      writeStoredPropertyId(context.memberId, context.tenantId, propertyId);
    }
  },

  syncActiveProperty(properties, { memberId, tenantId }) {
    if (properties.length === 0) {
      set({ activePropertyId: null });
      writeStoredPropertyId(memberId, tenantId, null);
      return null;
    }

    const stored = readStoredPropertyId(memberId, tenantId);
    const found = stored ? properties.find((p) => p.property_id === stored) : undefined;
    const match = found ?? properties[0];
    if (!match) return null;

    if (get().activePropertyId !== match.property_id) {
      set({ activePropertyId: match.property_id });
    }
    writeStoredPropertyId(memberId, tenantId, match.property_id);
    return match;
  },

  clear() {
    set({ activePropertyId: null });
  },
}));
